// ============================================================================
// Edit my rotation — template editor (AFB-0063 R-4 front end).
//
// The template is DERIVED: every facility the AM owns appears automatically
// (assignment drives the route), split into two groups:
//   • Scheduled — grouped by day (existing behaviour).
//   • Unscheduled / As able — everything with no day. Collapsible, sorted by
//     facility name, shows a count; must stay usable at ~30+ rows.
//
// Row rules:
//   • coverage 'own'   — CANNOT be removed (no affordance at all). Moving it
//     off a day calls am_route_set_schedule(day => NULL) → Unscheduled.
//   • coverage 'assist'— removable; badge "Helping <owner first name>".
//     Remove sets active=false on that row only.
//   • coverage 'stop'  — non-facility stop; removable.
// Owner rows show who is assisting (D2), read from the same view load.
// ============================================================================

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarRange, ChevronDown, ChevronRight, Plus, Users } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  ErrorState,
  PageHeader,
  RagDot,
  Skeleton,
  useToast,
} from '../../shared/components';
import { ServiceStateChip } from '../account-management/stop-service-parts';
import {
  CADENCE_LABELS,
  WEEKDAYS,
  useFacilityDirectory,
  useTemplate,
  type DayOfWeek,
  type RouteCadence,
  type TemplateRow,
  type WeekParity,
} from './hooks/use-am-routes-data';
import {
  stopMatchesWeek,
  weekOfMonth,
  type Parity,
  type WeekTarget,
} from './lib/route-week-math';
import AdHocModal from './components/ad-hoc-modal';
import { errorMessage } from '../../shared/errors';

// Week-of-month ordinals an AM can preview / schedule against.
const WEEKS_OF_MONTH = [1, 2, 3, 4, 5] as const;

const CADENCES: RouteCadence[] = [
  'Weekly',
  'Biweekly',
  'Monthly',
  'Bimonthly',
  'As_Needed',
];

export function RouteTemplateEditor() {
  const { employee, profile } = useAuth();
  const toast = useToast();
  const myEid = employee?.employee_id ?? profile?.employee_id ?? null;
  const tpl = useTemplate(myEid);
  const directory = useFacilityDirectory();
  const [showAdd, setShowAdd] = useState(false);
  const [unschedOpen, setUnschedOpen] = useState(true);

  // Scheduled rows grouped by day; everything with no day → Unscheduled.
  const { dayGroups, unscheduled } = useMemo(() => {
    const byDay = new Map<string, TemplateRow[]>();
    const unsched: TemplateRow[] = [];
    for (const r of tpl.rows) {
      if (!r.day_of_week) {
        unsched.push(r);
      } else {
        if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, []);
        byDay.get(r.day_of_week)!.push(r);
      }
    }
    const groups: Array<{ key: string; label: string; rows: TemplateRow[] }> = [];
    for (const d of WEEKDAYS) {
      const rows = byDay.get(d);
      if (rows && rows.length) {
        rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        groups.push({ key: d, label: d, rows });
      }
    }
    // Unscheduled sorts by facility name by default (3a).
    unsched.sort((a, b) =>
      (a.facility_name ?? a.stop_label ?? '').localeCompare(
        b.facility_name ?? b.stop_label ?? '',
      ),
    );
    return { dayGroups: groups, unscheduled: unsched };
  }, [tpl.rows]);

  // D2: active assists other people hold on facilities I own, by facility.
  const assistsByFacility = useMemo(() => {
    const map = new Map<string, TemplateRow[]>();
    for (const a of tpl.assistsIn) {
      if (!a.facility_id) continue;
      if (!map.has(a.facility_id)) map.set(a.facility_id, []);
      map.get(a.facility_id)!.push(a);
    }
    return map;
  }, [tpl.assistsIn]);

  const scheduledCount = tpl.rows.length - unscheduled.length;

  async function save(fn: () => Promise<unknown>, okMsg: string) {
    try {
      await fn();
      toast.success(okMsg);
    } catch (e) {
      toast.error(errorMessage(e, 'Save failed.'));
    }
  }

  function rowKey(r: TemplateRow): string {
    return r.assignment_id ?? `own-${r.facility_id}`;
  }

  const renderRow = (row: TemplateRow) => (
    <AssignmentRow
      key={rowKey(row)}
      row={row}
      myEid={myEid}
      assists={row.facility_id ? assistsByFacility.get(row.facility_id) ?? [] : []}
      onSchedule={(input, msg) =>
        save(
          () =>
            tpl.setSchedule({
              facility_id: row.facility_id!,
              day_of_week: input.day_of_week,
              cadence: input.cadence,
              week_parity: input.week_parity,
            }),
          msg,
        )
      }
      onPatch={(patch, msg) => {
        if (row.assignment_id) {
          void save(() => tpl.updateAssignment(row.assignment_id!, patch), msg);
        }
      }}
      onRemove={
        row.is_removable && row.assignment_id
          ? () =>
              save(
                () => tpl.setActive(row.assignment_id!, false),
                row.coverage === 'assist' ? 'Assist removed' : 'Stop removed',
              )
          : undefined
      }
    />
  );

  return (
    <div className="mx-auto max-w-3xl p-4 pb-24 sm:p-6">
      <Link
        to="/route"
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-tcs-text-secondary hover:text-tcs-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to today's route
      </Link>

      <PageHeader
        title="Edit my rotation"
        meta={
          tpl.isLoading
            ? 'Loading…'
            : `${scheduledCount} scheduled · ${unscheduled.length} unscheduled`
        }
        actions={
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            icon={<Plus className="h-3.5 w-3.5" />}
          >
            Add stop
          </Button>
        }
      />

      <div className="mt-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
        Every facility you manage is on this list automatically. Changes here
        apply to <strong>future days</strong>; today's route, if you've already
        edited it, stays as-is.
      </div>

      {!tpl.isLoading && !tpl.error && tpl.rows.length > 0 && (
        <RotationPreview rows={tpl.rows} />
      )}

      {tpl.error && (
        <div className="mt-4">
          <ErrorState body={tpl.error} onRetry={tpl.refetch} />
        </div>
      )}

      {tpl.isLoading && (
        <div className="mt-4 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} block height={56} className="w-full rounded-lg" />
          ))}
        </div>
      )}

      {!tpl.isLoading &&
        dayGroups.map((g) => (
          <section key={g.key} className="mt-5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              {g.label}
            </h2>
            <div className="mt-2 space-y-2">{g.rows.map(renderRow)}</div>
          </section>
        ))}

      {/* Unscheduled / As able — collapsible, count, name-sorted (3a). */}
      {!tpl.isLoading && (
        <section className="mt-6">
          <button
            type="button"
            onClick={() => setUnschedOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2.5 text-left"
          >
            <span className="flex items-center gap-2">
              {unschedOpen ? (
                <ChevronDown className="h-4 w-4 text-tcs-text-muted" />
              ) : (
                <ChevronRight className="h-4 w-4 text-tcs-text-muted" />
              )}
              <span className="text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
                Unscheduled / As able
              </span>
            </span>
            <span className="text-xs font-medium text-tcs-text-secondary">
              {unscheduled.length}
            </span>
          </button>
          {unschedOpen && (
            <div className="mt-2 space-y-2">
              {unscheduled.length === 0 ? (
                <div className="rounded-lg border border-dashed border-tcs-border bg-tcs-raised p-4 text-center text-sm text-tcs-text-secondary">
                  Nothing waiting. Every facility you manage is on a day.
                </div>
              ) : (
                unscheduled.map(renderRow)
              )}
            </div>
          )}
        </section>
      )}

      {showAdd && myEid && (
        <AdHocModal
          onClose={() => setShowAdd(false)}
          template={{
            myEid,
            directory: directory.facilities,
            directoryLoading: directory.loading,
            onScheduleOwn: async (input) => {
              await tpl.setSchedule(input);
              setShowAdd(false);
              toast.success(
                input.day_of_week
                  ? `Scheduled for ${input.day_of_week}`
                  : 'Added to Unscheduled',
              );
            },
            onAddAssist: async (input) => {
              await tpl.createAssist(input, myEid);
              setShowAdd(false);
              toast.success('Added as helper');
            },
          }}
        />
      )}
    </div>
  );
}

// ----- Multi-week preview (week-of-month selector) --------------------------
// Drives the SAME cadence / parity match logic the live week view uses
// (route-week-math.stopMatchesWeek) against the full template, but for a
// chosen week-of-month instead of CURRENT_DATE — so an AM can preview how
// weeks 1–5 actually render (incl. Monthly circuits and Weekly exclusions)
// before it goes live. The A/B affordance only appears when biweekly stops
// exist (otherwise parity is irrelevant).

function RotationPreview({ rows }: { rows: TemplateRow[] }) {
  const [week, setWeek] = useState<number>(() => weekOfMonth(new Date()));
  const [parity, setParity] = useState<Parity>('A');

  const hasBiweekly = useMemo(
    () => rows.some((r) => r.active && r.cadence === 'Biweekly'),
    [rows],
  );

  const target: WeekTarget = { parity, weekOfMonth: week, weekStart: '' };

  const byDay = useMemo(() => {
    const map = new Map<DayOfWeek, TemplateRow[]>();
    for (const day of WEEKDAYS) map.set(day, []);
    for (const r of rows) {
      if (!r.active || !r.day_of_week) continue;
      // TemplateRow structurally satisfies WeekRuleRow (cadence / week_parity /
      // week_of_month / is_as_able / exclude_weeks), so pass it straight in.
      if (!stopMatchesWeek(r, target)) continue;
      map.get(r.day_of_week as DayOfWeek)?.push(r);
    }
    for (const day of WEEKDAYS) {
      map.get(day)!.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, target.weekOfMonth, target.parity]);

  const total = WEEKDAYS.reduce((n, d) => n + (byDay.get(d)?.length ?? 0), 0);
  const visibleDays = WEEKDAYS.filter((d) => (byDay.get(d)?.length ?? 0) > 0);

  return (
    <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold text-tcs-text">
          <CalendarRange className="h-4 w-4 text-tcs-text-secondary" />
          Preview week
        </h2>
        {hasBiweekly && (
          <div className="inline-flex overflow-hidden rounded-lg border border-tcs-border">
            {(['A', 'B'] as Parity[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setParity(p)}
                className={`px-2.5 py-1 text-xs font-medium ${
                  parity === p
                    ? 'bg-tcs-inverse text-tcs-inverse-ink'
                    : 'bg-tcs-raised text-tcs-text-secondary'
                }`}
              >
                Wk {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 inline-flex rounded-lg border border-tcs-border bg-tcs-raised p-0.5">
        {WEEKS_OF_MONTH.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setWeek(w)}
            aria-pressed={week === w}
            className={`h-9 w-12 rounded-md text-sm font-semibold transition-colors ${
              week === w
                ? 'bg-tcs-inverse text-tcs-inverse-ink'
                : 'text-tcs-text-secondary hover:text-tcs-text'
            }`}
          >
            {w}
          </button>
        ))}
      </div>

      <p className="mt-2 text-xs text-tcs-text-muted">
        What renders on <strong>week {week}</strong> of the month
        {hasBiweekly ? ` · biweekly week ${target.parity}` : ''} · {total} stop
        {total === 1 ? '' : 's'}.
      </p>

      {total === 0 ? (
        <div className="mt-2 rounded-lg border border-dashed border-tcs-border bg-tcs-raised p-4 text-center text-xs text-tcs-text-secondary">
          No scheduled stops on week {week}.
        </div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {visibleDays.map((day) => (
            <div
              key={day}
              className="rounded-lg border border-tcs-border bg-tcs-raised p-2"
            >
              <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                {day}
              </div>
              <ul className="mt-1 space-y-1">
                {(byDay.get(day) ?? []).map((r) => (
                  <li
                    key={r.assignment_id ?? `own-${r.facility_id}`}
                    className="flex items-center gap-2 text-sm"
                  >
                    <RagDot status={r.account_status_rag} />
                    <ServiceStateChip state={r.service_state} size="dot" />
                    <span className="min-w-0 flex-1 truncate text-tcs-text">
                      {r.facility_name ?? r.stop_label}
                    </span>
                    <span className="shrink-0 rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] font-medium text-tcs-text-secondary">
                      {r.cadence ? CADENCE_LABELS[r.cadence] : ''}
                      {r.cadence === 'Monthly' && r.week_of_month
                        ? ` · wk ${r.week_of_month}`
                        : ''}
                      {r.cadence === 'Biweekly' &&
                      r.week_parity &&
                      r.week_parity !== 'A_B'
                        ? ` · ${r.week_parity}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ----- One editable row ------------------------------------------------------

function firstName(full: string | null | undefined): string {
  return (full ?? '').trim().split(/\s+/)[0] || 'the AM';
}

function AssignmentRow({
  row,
  myEid,
  assists,
  onSchedule,
  onPatch,
  onRemove,
}: {
  row: TemplateRow;
  myEid: string | null;
  // Active assists on this facility by OTHER employees (rendered on own rows).
  assists: TemplateRow[];
  // Ownership scheduling — routes through am_route_set_schedule (works with
  // or without an existing schedule row).
  onSchedule: (
    input: {
      day_of_week: DayOfWeek | null;
      cadence?: RouteCadence | null;
      week_parity?: WeekParity | null;
    },
    msg: string,
  ) => void;
  // Direct row patch — only valid when assignment_id exists (assist/stop
  // rows, or own rows that already have a schedule row).
  onPatch: (patch: Record<string, unknown>, msg: string) => void;
  // Present only for removable rows (assist/stop). Ownership rows never get
  // a remove affordance — not even disabled.
  onRemove?: () => void;
}) {
  const isOwn = row.coverage === 'own';
  const dayValue = row.day_of_week ?? 'unscheduled';
  const otherAssists = assists.filter((a) => a.am_eid !== myEid);

  function changeDay(v: string) {
    const day = v === 'unscheduled' ? null : (v as DayOfWeek);
    if (isOwn) {
      onSchedule(
        { day_of_week: day, cadence: row.cadence, week_parity: row.week_parity },
        day ? `Moved to ${day}` : 'Moved to Unscheduled',
      );
    } else {
      onPatch(
        day
          ? { is_as_able: false, day_of_week: day }
          : { is_as_able: true, day_of_week: null },
        day ? `Moved to ${day}` : 'Moved to Unscheduled',
      );
    }
  }

  function changeCadence(v: string) {
    const cadence = v as RouteCadence;
    const nextParity: WeekParity | null =
      cadence === 'Biweekly' ? row.week_parity ?? 'A' : null;
    if (isOwn) {
      onSchedule(
        { day_of_week: row.day_of_week ?? null, cadence, week_parity: nextParity },
        'Cadence updated',
      );
    } else {
      const patch: Record<string, unknown> = { cadence };
      if (cadence !== 'Biweekly') patch.week_parity = null;
      else if (!row.week_parity) patch.week_parity = 'A';
      onPatch(patch, 'Cadence updated');
    }
  }

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="flex items-center gap-2">
        <RagDot status={row.account_status_rag} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-tcs-text">
          {row.facility_name ?? row.stop_label}
        </span>
        {/* Scheduling a stop at a paused site is a decision, not an accident.
            The template legitimately keeps the row — it is the plan, and the
            plan survives a pause — so the badge is the only thing that tells
            an AM the site is not currently being served. */}
        <ServiceStateChip state={row.service_state} size="sm" />
        {row.coverage === 'assist' && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
            <Users className="h-3 w-3" />
            Helping {firstName(row.owner_name)}
          </span>
        )}
        {row.coverage === 'stop' && (
          <span className="shrink-0 rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] font-semibold text-tcs-text-secondary">
            Stop
          </span>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 text-xs font-medium text-tcs-text-secondary hover:text-rag-red"
          >
            Remove
          </button>
        )}
      </div>

      {/* D2 — who else visits a facility I own, from the view (no per-row query). */}
      {isOwn && otherAssists.length > 0 && (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-violet-700">
          <Users className="h-3.5 w-3.5" />
          {otherAssists
            .map(
              (a) =>
                `${a.am_name ?? 'Someone'} also visits${
                  a.cadence ? ` — ${CADENCE_LABELS[a.cadence].toLowerCase()}` : ''
                }`,
            )
            .join(' · ')}
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <MiniSelect label="Day" value={dayValue} onChange={changeDay}>
          {WEEKDAYS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          <option value="unscheduled">Unscheduled</option>
        </MiniSelect>

        <MiniSelect
          label="Cadence"
          value={row.cadence ?? 'Weekly'}
          onChange={changeCadence}
        >
          {CADENCES.map((c) => (
            <option key={c} value={c}>
              {CADENCE_LABELS[c]}
            </option>
          ))}
        </MiniSelect>

        {row.cadence === 'Biweekly' && (
          <MiniSelect
            label="Week"
            value={row.week_parity ?? 'A'}
            onChange={(v) => {
              if (isOwn) {
                onSchedule(
                  {
                    day_of_week: row.day_of_week ?? null,
                    cadence: row.cadence,
                    week_parity: v as WeekParity,
                  },
                  'Week updated',
                );
              } else {
                onPatch({ week_parity: v as WeekParity }, 'Week updated');
              }
            }}
          >
            <option value="A">Week A</option>
            <option value="B">Week B</option>
            <option value="A_B">Both weeks</option>
          </MiniSelect>
        )}

        {/* week-of-month / exclude-weeks need a concrete row — own rows have
            one as soon as they're scheduled (the RPC creates it). */}
        {row.assignment_id && row.cadence === 'Monthly' && (
          <MiniSelect
            label="Week of month"
            value={row.week_of_month != null ? String(row.week_of_month) : 'every'}
            onChange={(v) =>
              onPatch(
                { week_of_month: v === 'every' ? null : Number(v) },
                v === 'every' ? 'Shows every week' : `Set to week ${v}`,
              )
            }
          >
            <option value="every">Every week</option>
            {WEEKS_OF_MONTH.map((w) => (
              <option key={w} value={String(w)}>
                Week {w}
              </option>
            ))}
          </MiniSelect>
        )}

        {row.assignment_id && row.cadence === 'Weekly' && (
          <ExcludeWeeksControl
            value={row.exclude_weeks ?? []}
            onChange={(weeks) =>
              onPatch(
                { exclude_weeks: weeks },
                weeks.length
                  ? `Skips week ${weeks.join(', ')}`
                  : 'Runs every week',
              )
            }
          />
        )}
      </div>
    </div>
  );
}

// Per-week suppression for a Weekly stop — toggle the weeks-of-month it sits
// out (e.g. a Week-1 circuit replaces the weekly backbone that week).
function ExcludeWeeksControl({
  value,
  onChange,
}: {
  value: number[];
  onChange: (weeks: number[]) => void;
}) {
  const toggle = (w: number) => {
    const set = new Set(value);
    if (set.has(w)) set.delete(w);
    else set.add(w);
    onChange(Array.from(set).sort((a, b) => a - b));
  };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-tcs-border bg-tcs-bg px-2 py-1 text-xs text-tcs-text">
      <span className="text-tcs-text-muted">Skip wks</span>
      <span className="inline-flex gap-0.5">
        {WEEKS_OF_MONTH.map((w) => {
          const on = value.includes(w);
          return (
            <button
              key={w}
              type="button"
              onClick={() => toggle(w)}
              aria-pressed={on}
              title={on ? `Week ${w} skipped` : `Skip week ${w}`}
              className={`h-6 w-6 rounded-md text-[11px] font-semibold transition-colors ${
                on
                  ? 'bg-st-danger-solid text-st-solid-ink'
                  : 'bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
              }`}
            >
              {w}
            </button>
          );
        })}
      </span>
    </span>
  );
}

function MiniSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-lg border border-tcs-border bg-tcs-bg px-2 py-1 text-xs text-tcs-text">
      <span className="text-tcs-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent font-medium text-tcs-text focus:outline-none"
      >
        {children}
      </select>
    </label>
  );
}
